import { apiClient } from './client'

export interface GroupCount { label: string; count: number }
export interface AnalyticsOverview {
  headcount: {
    active: number
    by_department: GroupCount[]
    by_designation: GroupCount[]
    by_employment_type: GroupCount[]
    by_gender: GroupCount[]
    by_age: GroupCount[]
  }
  attendance_today: { present: number; on_leave: number; active: number }
  leave_this_month: GroupCount[]
  payroll: { period: string; total_net: number } | null
}

export async function getOverview(): Promise<AnalyticsOverview> {
  return (await apiClient.get<AnalyticsOverview>('/analytics/overview')).data
}

export interface LeavePolicy {
  default_leave_quotas?: Record<string, number> | null
  leave_accrual?: {
    enabled?: boolean
    monthly?: Record<string, number>
    carry_forward_cap?: Record<string, number>
  } | null
  fiscal_start_month?: number | null
  // How many days back employees may apply for leave (0 = no backdating).
  backdate_days?: number | null
}
export async function getLeavePolicy(): Promise<LeavePolicy> {
  return (await apiClient.get<LeavePolicy>('/organization/leave-policy')).data
}
export async function setLeavePolicy(data: LeavePolicy): Promise<LeavePolicy> {
  return (await apiClient.put<LeavePolicy>('/organization/leave-policy', data)).data
}

export interface AttendanceDrilldownTiles {
  active_employees: number
  planned_shifts: number
  upcoming_shifts: number
  presents: number
  absents: number
  holiday: number
  leaves: number
  days_off: number
  wfh: number
  late_arrivals: number
  early_left: number
}

export interface DrilldownPerson {
  user_id: string
  name: string
  checkin_time: string
  minutes: number
}

/** One employee behind an Attendance Drill Down tile or a Head Count bar. */
export interface HeadcountMember {
  user_id: string
  name: string
  avatar_url: string | null
  department?: string | null
  designation?: string | null
  checkin_time?: string | null
  checkout_time?: string | null
  leave_type?: string | null
  minutes?: number
}

export type AttendanceDrilldownCategory = keyof AttendanceDrilldownTiles

export interface AttendanceDrilldown {
  date: string
  tiles: AttendanceDrilldownTiles
  members: Record<AttendanceDrilldownCategory, HeadcountMember[]>
  early_risers: DrilldownPerson[]
  late_comers: DrilldownPerson[]
}

export async function getAttendanceDrilldown(on?: string): Promise<AttendanceDrilldown> {
  return (await apiClient.get<AttendanceDrilldown>('/analytics/attendance-drilldown', {
    params: on ? { on } : {},
  })).data
}

export type HeadcountDimension = 'department' | 'designation' | 'employment_type' | 'gender' | 'age'

export async function getHeadcountMembers(dimension: HeadcountDimension, value: string): Promise<HeadcountMember[]> {
  return (await apiClient.get<HeadcountMember[]>('/analytics/headcount-members', {
    params: { dimension, value },
  })).data
}

export interface SnapshotPerson {
  user_id: string
  name: string
  avatar_url: string | null
}
export interface SnapshotJoiner extends SnapshotPerson { date_of_joining: string }
export interface SnapshotAnniversary extends SnapshotPerson { date_of_joining: string; years: number }
export interface SnapshotConfirmation extends SnapshotPerson { probation_end_date: string }
export interface SnapshotSeparation extends SnapshotPerson { separation_date: string }
export interface SnapshotResignation extends SnapshotPerson { resignation_date: string }

export interface PeopleSnapshot {
  birthdays: SnapshotPerson[]
  new_joiners: SnapshotJoiner[]
  upcoming_anniversaries: SnapshotAnniversary[]
  my_team: SnapshotPerson[]
  separations_30d: SnapshotSeparation[]
  confirmations_30d: SnapshotConfirmation[]
  resignations_this_month: SnapshotResignation[]
}

export async function getPeopleSnapshot(withinDays = 30): Promise<PeopleSnapshot> {
  return (await apiClient.get<PeopleSnapshot>('/analytics/people-snapshot', {
    params: { within_days: withinDays },
  })).data
}

export interface AttendanceTimeseriesPoint {
  key: string
  label: string
  present: number
  absent: number
  leave: number
  wfh: number
  late: number
  worked_minutes: number
  worked_hours: number
}

export interface AttendanceSummaryTotals {
  employees: number
  scheduled_working_days: number
  present_days: number
  absent_days: number
  leave_days: number
  wfh_days: number
  late_days: number
  late_minutes: number
  overtime_minutes: number
  worked_minutes: number
  worked_hours: number
  overtime_hours: number
  attendance_rate: number
  avg_hours_per_present_day: number
  casual_days: number
  sick_days: number
  urgent_days: number
  /** Per-leave-type day counts across the org, keyed by whatever types are
   * actually configured (e.g. "annual") — a superset of the three named
   * fields above, so a custom type is never silently dropped. */
  leave_type_days: Record<string, number>
}

/** One employee's rollup for the selected range — mirrors the backend's
 * EmployeeSummary.to_dict() (see hr_analytics_service.py). */
export interface EmployeeAttendanceSummary {
  user_id: string
  name: string
  email: string
  employee_id: string | null
  department: string | null
  designation: string | null
  role: string
  date_of_joining: string | null
  scheduled_working_days: number
  /** Total scheduled/expected minutes across the range (a day-count times
   * duration sum) — distinct from scheduled_working_days (a day count). */
  expected_minutes: number
  /** Sum of each day's shortfall; leave, holidays and in-progress days excluded. */
  short_minutes: number
  early_left_days: number
  present_days: number
  completed_days: number
  overtime_days: number
  less_hours_days: number
  wfh_days: number
  leave_days: number
  casual_days: number
  sick_days: number
  urgent_days: number
  leave_type_days: Record<string, number>
  absent_days: number
  holiday_days: number
  off_days: number
  late_days: number
  late_minutes: number
  overtime_minutes: number
  overtime_hours: number
  worked_minutes: number
  worked_hours: number
  avg_hours_per_present_day: number
  attendance_rate: number
}

export interface AttendanceSummary {
  start: string
  end: string
  employees: EmployeeAttendanceSummary[]
  totals: AttendanceSummaryTotals
}

export async function getAttendanceSummary(
  start: string, end: string, userIds?: string[], departmentId?: string,
): Promise<AttendanceSummary> {
  return (await apiClient.get<AttendanceSummary>('/analytics/attendance-summary', {
    params: { start, end, user_ids: userIds, department_id: departmentId },
    paramsSerializer: { indexes: null }, // repeat ?user_ids= for each
  })).data
}

export async function getAttendanceTimeseries(
  start: string, end: string, granularity: 'day' | 'week' | 'month' = 'day',
): Promise<AttendanceTimeseriesPoint[]> {
  return (await apiClient.get<AttendanceTimeseriesPoint[]>('/analytics/attendance-timeseries', {
    params: { start, end, granularity },
  })).data
}
